/**
 * PM Command Center - Option E: 3-Tab Structure
 *
 * Tab 1: Quotes/Projects - Intake queue with assignment workflow
 * Tab 2: Work Orders - Create/edit work orders, feeds into Project Tracker
 * Tab 3: Active Projects - Table view + workspace for project management
 */

import { useState, useMemo, useEffect, useCallback } from "react";
import { useMsal } from "@azure/msal-react";
import { InteractionRequiredAuthError } from "@azure/msal-browser";
import PMReworkWidget from "./PMReworkWidget";
// Tour disabled for now
// import { usePMCommandCenterTour, TourButton } from "./PMCommandCenterTour";

const apiScopes = [
  "api://2350508b-6d67-408a-9c55-c5040357d90d/b351e950-d526-4ee4-b95b-03f6ec89cd3d/access_as_user",
];

// PM Team members
const PM_TEAM = [
  { id: "amanda", name: "Amanda" },
  { id: "trent", name: "Trent" },
  { id: "gary", name: "Gary" },
  { id: "janet", name: "Janet" },
  { id: "chris", name: "Chris" },
];

// Current user (for demo)
const CURRENT_USER = { id: "mark", name: "Mark", role: "PM" };

// Mock data: Quotes in pipeline (not kicked off yet)
const MOCK_PIPELINE_QUOTES = [
  {
    id: "q1",
    quote_number: "Q-24-1245",
    customer_name: "Johnson & Johnson",
    description: "Band-Aid Box Redesign",
    sales_rep: "Jennifer Wilson",
    quoted_date: "2024-12-06",
    estimated_ship: "2025-01-10",
    status: "quoted",
  },
  {
    id: "q2",
    quote_number: "Q-24-1246",
    customer_name: "Colgate-Palmolive",
    description: "Toothpaste Tube Sleeves",
    sales_rep: "Mike Stevens",
    quoted_date: "2024-12-05",
    estimated_ship: "2025-01-12",
    status: "quoted",
  },
  {
    id: "q3",
    quote_number: "Q-24-1247",
    customer_name: "Procter & Gamble",
    description: "Pampers Display Stand",
    sales_rep: "Jennifer Wilson",
    quoted_date: "2024-12-04",
    estimated_ship: "2025-01-15",
    status: "quoted",
  },
];

// Mock data: Kicked off projects (from email intake)
const MOCK_KICKOFFS = [
  {
    id: "k1",
    quote_number: "Q-24-1234",
    customer_name: "Procter & Gamble",
    description: "Tide Pods Holiday Packaging - Q1 2025 refresh",
    sales_rep: "Jennifer Wilson",
    kicked_off_at: new Date(Date.now() - 2 * 60 * 60 * 1000).toISOString(), // 2 hours ago
    ship_date: "2024-12-20",
    quantities: { shrinks: 12, cartons: 8, labels: 0 },
    attachments: [{ name: "Project_Brief_TidePods.docx", size: "245 KB" }],
    assigned_to: null,
    status: "unassigned",
  },
  {
    id: "k2",
    quote_number: "Q-24-1235",
    customer_name: "Kraft Heinz",
    description: "Mac & Cheese Holiday Edition",
    sales_rep: "Mike Stevens",
    kicked_off_at: new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString(), // Yesterday
    ship_date: "2024-12-22",
    quantities: { shrinks: 0, cartons: 8, labels: 0 },
    attachments: [{ name: "Kraft_Brief_Dec2024.docx", size: "312 KB" }],
    assigned_to: null,
    status: "unassigned",
  },
  {
    id: "k3",
    quote_number: "Q-24-1236",
    customer_name: "Nestle",
    description: "Coffee Bag Premium Line",
    sales_rep: "Jennifer Wilson",
    kicked_off_at: new Date(Date.now() - 48 * 60 * 60 * 1000).toISOString(), // 2 days ago
    ship_date: "2024-12-28",
    quantities: { shrinks: 0, cartons: 10, labels: 0 },
    attachments: [{ name: "Nestle_Coffee_Brief.docx", size: "189 KB" }],
    assigned_to: null,
    status: "unassigned",
  },
];

// Mock data: Assigned but awaiting work order
const MOCK_ASSIGNED = [
  {
    id: "a1",
    quote_number: "Q-24-1230",
    customer_name: "Unilever",
    description: "Dove Body Wash Labels - Spring Collection",
    sales_rep: "Jennifer Wilson",
    ship_date: "2024-12-28",
    quantities: { shrinks: 0, cartons: 6, labels: 50 },
    assigned_to: { id: "janet", name: "Janet" },
    assigned_at: "2024-12-05",
    art_status: "awaiting",
    status: "assigned",
  },
  {
    id: "a2",
    quote_number: "Q-24-1228",
    customer_name: "Nestle",
    description: "Hot Cocoa Box Winter Seasonal",
    sales_rep: "Mike Stevens",
    ship_date: "2025-01-05",
    quantities: { shrinks: 6, cartons: 12, labels: 0 },
    assigned_to: { id: "mark", name: "Mark" },
    assigned_at: "2024-12-04",
    art_status: "received",
    attachments: [
      { name: "Nestle_HotCocoa_Art.ai", size: "4.2 MB" },
      { name: "Nestle_Dieline.pdf", size: "1.1 MB" },
    ],
    status: "assigned",
  },
];

// Mock data: Work orders in progress
const MOCK_WORK_ORDER_DRAFTS = [
  {
    id: "wo1",
    project_id: "P-24-0089",
    quote_number: "Q-24-1225",
    customer_name: "Procter & Gamble",
    description: "Tide Pods Holiday Packaging",
    created_by: { id: "mark", name: "Mark" },
    created_at: new Date().toISOString(),
    progress: 60,
    status: "draft",
  },
];

// Mock data: Active projects
const MOCK_ACTIVE_PROJECTS = [
  {
    id: "p1",
    project_id: "P-24-0085",
    quote_number: "Q-24-1220",
    customer_name: "Nestle",
    description: "Coffee Bag Redesign - Premium line",
    status: "production",
    ship_date: "2024-12-15",
    progress: 65,
    assigned_to: { id: "mark", name: "Mark" },
    orders: [
      { order_number: "ORD-2401", work_orders: ["WO-2401-A", "WO-2401-B"] }
    ],
  },
  {
    id: "p2",
    project_id: "P-24-0086",
    quote_number: "Q-24-1221",
    customer_name: "Procter & Gamble",
    description: "Pampers Display Stand - Retail",
    status: "proofing",
    ship_date: "2024-12-16",
    progress: 80,
    assigned_to: { id: "janet", name: "Janet" },
    proof_status: "awaiting_approval",
    orders: [
      { order_number: "ORD-2402", work_orders: ["WO-2402-A"] }
    ],
  },
  {
    id: "p3",
    project_id: "P-24-0087",
    quote_number: "Q-24-1222",
    customer_name: "Unilever",
    description: "Axe Body Spray Cartons - Limited Ed",
    status: "prep",
    ship_date: "2024-12-18",
    progress: 30,
    assigned_to: { id: "mark", name: "Mark" },
    orders: [],
  },
  {
    id: "p4",
    project_id: "P-24-0088",
    quote_number: "Q-24-1223",
    customer_name: "Kraft Heinz",
    description: "Ketchup Bottle Labels - Standard",
    status: "production",
    ship_date: "2024-12-21",
    progress: 45,
    assigned_to: { id: "trent", name: "Trent" },
    orders: [
      { order_number: "ORD-2403", work_orders: ["WO-2403-A"] }
    ],
  },
  {
    id: "p5",
    project_id: "P-24-0084",
    quote_number: "Q-24-1219",
    customer_name: "Colgate-Palmolive",
    description: "Mouthwash Bottle Sleeves",
    status: "finishing",
    ship_date: "2024-12-14",
    progress: 90,
    assigned_to: { id: "gary", name: "Gary" },
    orders: [
      { order_number: "ORD-2400", work_orders: ["WO-2400-A", "WO-2400-B"] }
    ],
  },
];

// Status configuration
const STATUS_CONFIG = {
  unassigned: { label: "Unassigned", color: "bg-red-500" },
  assigned: { label: "Assigned", color: "bg-yellow-500" },
  prep: { label: "Prep", color: "bg-blue-500" },
  production: { label: "Production", color: "bg-green-500" },
  proofing: { label: "Proofing", color: "bg-purple-500" },
  finishing: { label: "Finishing", color: "bg-orange-500" },
  complete: { label: "Complete", color: "bg-slate-200" },
};

// Workspace tabs for project detail
const WORKSPACE_TABS = [
  { id: "overview", label: "Overview" },
  { id: "art_files", label: "Art Files" },
  { id: "orders", label: "Orders/WOs" },
  { id: "proofs", label: "Proofs" },
  { id: "notes", label: "Notes" },
];

// Sales reps dropdown
const SALES_REPS = [
  "Amy Aureli", "Bob Jennings", "Chris Schwalbach", "David VanTyle",
  "Eric Garcia", "Garren Parker", "Leah Mattingly", "Mike Corbett", "Susan Arnold"
];

// Work order dropdown options (from Excel DATA sheet)
const WO_OPTIONS = {
  poaTypes: [
    "2D Research Panel", "Back Label", "Cap Sticker", "Carton (Hero Die)", "Carton",
    "Case Wrap", "Display Parts", "Flow Wrap", "Foil Transfer", "Front Label",
    "Ink Transfer", "Poly Overwrap", "Pallet Skirt Print", "Poly Bag", "Poster Print",
    "Shelf Ready Carton", "Shrink Sleeve", "Snack Bag", "Stand-Up Pouch", "Tetra Pak",
    "Tray Insert", "Tray/PDQ", "Tube Wrap Label", "UPC/NFS Sticker", "Wrap Label",
    "Stick Pack", "Sachet"
  ],
  fileLocations: ["Supplied Folder", "Exact Reprint", "Reprint w/ Changes", "3DC to Create", "Other", "N/A"],
  colorTargets: ["Match PMS in File", "Physical Target", "Run to File", "Match Previous", "Other", "N/A"],
  substrates: [
    "--- PRESSURE SENSITIVE LABEL ---",
    "Gloss Blockout Vinyl", "Gloss Oris Vinyl", "Matte Blockout Vinyl", "6 mil Wall Vinyl",
    "6 mil Gloss Vinyl", "6 mil Matte Vinyl", "Rainbow Holo Foil PSL", "8512 2mil Mirror Poly PSL",
    "8506 2mil Clear Poly PSL", "Dull Foil PSL", "Matte Clear PSL",
    "--- BOARD & CORRUGATE ---",
    "8300 Gloss Transfer to 14pt C1S", "8301 Matte Transfer to 14pt C1S",
    "8300 Gloss Transfer to 18pt C1S", "8301 Matte Transfer to 18pt C1S",
    "Direct Print to 14pt C1S", "Direct Print to 18pt C1S",
    "6 mil Gloss Vinyl to E-Flute", "6 mil Matte Vinyl to E-Flute",
    "--- SHRINK FILM ---",
    "8517 2mil Clear PETG Shrink Reverse Print", "8527 2mil Clear PVC Shrink Surface Print (Satin)",
    "--- TRANSFER MEDIA ---",
    "Oris Transfer Media (clear liner)", "8300 Gloss Transfer Media", "8301 Matte Transfer Media"
  ],
  finishes: [
    "50:.5 Satin SPOT", "50:.5 Satin OVERALL", "50:1 Standard Matte SPOT", "50:1 Standard Matte OVERALL",
    "50:2 Heavy Matte SPOT", "50:2 Heavy Matte OVERALL", "50:4 Super Heavy Matte SPOT",
    "50:4 Super Heavy Matte OVERALL", "100:.75 Gallo Sim AQC OVERALL", "50:.8 Jergens Satin OVERALL",
    "Hi-rez Sim AQC OVERALL", "Spray Matte (Rattle Can)", "Spray Matte (Gun/3D)",
    "Platinum Matte (Satin/Aqueous)", "Karess Soft-Touch Laminate", "N/A", "Other"
  ],
  specialEffects: [
    "Emboss (plate)", "Deboss (plate)", "Emboss + Deboss (combo plate)", "Foil Stamp (plate)",
    "Foil Stamp + Emboss (plate)", "Foil + Emboss + Deboss (combo plate)", "Ink Transfer/Rubdown",
    "Foil Transfer/Rubdown (surface)", "Foil Transfer/Rubdown (behind)", "UV - Spot Gloss",
    "UV - Spot Gloss / Emboss", "UV - Tactile Only", "Liquid Metal (flood)", "Laminate Holo Foil", "N/A"
  ],
  shippingMethods: [
    "Priority Overnight (typically by 10:30am)", "1st Overnight (typically by 8am)",
    "International Priority", "Saturday Delivery", "Ground", "Local Courier (Pinpoint 513.421.1121)",
    "International Courier (PakAir)", "Salesperson to deliver", "Customer Pickup at Front Desk"
  ],
  // Paint/Model specific
  structureTypes: ["Bottle", "Cap/Lid", "Transition", "Actuator/Pump", "Jar", "Tube", "Carton", "Color Button", "Other"],
  opacities: ["Opaque", "Transparent", "Translucent", "Supplied"],
  paintFinishes: ["Matte", "Satin", "Gloss", "Existing", "Other"],
  paintEffects: ["Supplied", "Metallic", "Chrome", "Pearl", "Dealer's Choice"]
};

export default function PMCenterOptionE({ onBackToApp }) {
  // MSAL Auth
  const { instance, accounts } = useMsal();

  // Acquire access token helper
  const acquireAccessToken = useCallback(async () => {
    if (accounts.length === 0) return null;
    try {
      const response = await instance.acquireTokenSilent({
        scopes: apiScopes,
        account: accounts[0],
      });
      return response.accessToken;
    } catch (err) {
      if (err instanceof InteractionRequiredAuthError) {
        const response = await instance.acquireTokenPopup({ scopes: apiScopes });
        return response.accessToken;
      }
      throw err;
    }
  }, [instance, accounts]);

  // Tour functionality (disabled for now)
  // const { startTour, shouldShowTour } = usePMCommandCenterTour();

  // Show tour on first visit (disabled)
  // useEffect(() => {
  //   if (shouldShowTour()) {
  //     const timer = setTimeout(() => startTour(), 500);
  //     return () => clearTimeout(timer);
  //   }
  // }, []);

  // Main tab state
  const [activeMainTab, setActiveMainTab] = useState("quotes"); // quotes, workorders, active

  // Loading and error state
  const [loading, setLoading] = useState({ intakes: true, workOrders: true });
  const [error, setError] = useState(null);

  // Tab 1 state - REAL DATA from email intakes
  const [emailIntakes, setEmailIntakes] = useState([]);
  const [kickoffs, setKickoffs] = useState([]); // Unassigned intakes
  const [assigned, setAssigned] = useState(MOCK_ASSIGNED); // Still using mock for now
  const [showAssignModal, setShowAssignModal] = useState(null);

  // Tab 2 state - REAL DATA from work orders queue
  const [workOrders, setWorkOrders] = useState([]);
  const [workOrderDrafts, setWorkOrderDrafts] = useState([]);

  // Load email intakes from API
  const loadEmailIntakes = useCallback(async () => {
    try {
      setLoading(prev => ({ ...prev, intakes: true }));
      const accessToken = await acquireAccessToken();
      if (!accessToken) return;

      const res = await fetch("/api/email-intake?limit=50", {
        headers: { Authorization: `Bearer ${accessToken}` },
      });

      if (!res.ok) throw new Error("Failed to load email intakes");

      const data = await res.json();
      setEmailIntakes(data.intakes || []);

      // Transform email intakes into kickoff format for the UI
      const kickoffData = (data.intakes || [])
        .filter(intake => intake.status === "extracted" || intake.status === "pending")
        .map(intake => ({
          id: intake.id,
          quote_number: intake.extracted_data?.quote_number || "—",
          customer_name: intake.extracted_data?.customer_name || intake.customer_name || "Unknown Customer",
          description: intake.extracted_data?.project_name || intake.email_subject || "Project from email",
          sales_rep: intake.extracted_data?.sales_rep || intake.email_from || "—",
          kicked_off_at: intake.email_received_at || intake.created_at,
          ship_date: intake.extracted_data?.ship_date || intake.ship_date,
          quantities: {
            shrinks: intake.extracted_data?.skus?.filter(s => s.poa_type?.toLowerCase().includes("shrink")).length || 0,
            cartons: intake.extracted_data?.skus?.filter(s => s.poa_type?.toLowerCase().includes("carton")).length || 0,
            labels: intake.extracted_data?.skus?.filter(s => s.poa_type?.toLowerCase().includes("label")).length || 0,
          },
          attachments: intake.attachments || [],
          assigned_to: null,
          status: "unassigned",
          _source: intake, // Keep original for later use
        }));

      setKickoffs(kickoffData);
    } catch (err) {
      console.error("Error loading email intakes:", err);
      setError(err.message);
    } finally {
      setLoading(prev => ({ ...prev, intakes: false }));
    }
  }, [acquireAccessToken]);

  // Load work orders from API
  const loadWorkOrders = useCallback(async () => {
    try {
      setLoading(prev => ({ ...prev, workOrders: true }));
      const accessToken = await acquireAccessToken();
      if (!accessToken) return;

      const res = await fetch("/api/pm-center/work-orders/queue", {
        headers: { Authorization: `Bearer ${accessToken}` },
      });

      if (!res.ok) throw new Error("Failed to load work orders");

      const data = await res.json();
      setWorkOrders(data.workOrders || []);

      // Separate drafts and active
      setWorkOrderDrafts((data.workOrders || []).filter(wo => wo.status === "draft"));
    } catch (err) {
      console.error("Error loading work orders:", err);
      setError(err.message);
    } finally {
      setLoading(prev => ({ ...prev, workOrders: false }));
    }
  }, [acquireAccessToken]);

  // Initial data load
  useEffect(() => {
    loadEmailIntakes();
    loadWorkOrders();
  }, [loadEmailIntakes, loadWorkOrders]);

  // Refresh every 60 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      loadEmailIntakes();
      loadWorkOrders();
    }, 60000);
    return () => clearInterval(interval);
  }, [loadEmailIntakes, loadWorkOrders]);
  const [selectedWorkOrder, setSelectedWorkOrder] = useState(null);
  const [woFormLayout, setWoFormLayout] = useState("accordion"); // "accordion" or "tabs"
  const [woFormTab, setWoFormTab] = useState("header"); // For tabbed layout
  const [expandedSections, setExpandedSections] = useState({
    header: true, shipping: true, proofing: false
  });
  const [skuCount, setSkuCount] = useState(1);

  // Helper to create a default POA object
  const createDefaultPoa = (id = 1, name = "") => ({
    id,
    name: name || `POA ${id}`,
    expanded: true,
    poaType: "",
    fileLocation: "Supplied Folder",
    colorTarget: "Match PMS in File",
    substrate: "",
    appliedFinish: "",
    specialEffects: "",
    workCenters: { epson: true, silver: false, os: false, uv: false },
    finishing: { opti: false, franklin: false },
    notes: "",
  });

  // SKU-centric state - each SKU has its own work requirements
  const [skus, setSkus] = useState([
    {
      id: 1,
      description: "",
      quantity: 1,
      expanded: true,
      // Work type toggles
      needsModel: false,
      needsPaint: false,
      needsGraphics: true,
      // Model/Structure fields
      structureType: "",
      structurePieces: 1,
      structureNotes: "",
      // Paint/Color fields
      paintFinish: "",
      paintOpacity: "",
      paintEffect: "",
      colorTarget: "",
      pantoneNumber: "",
      paintNotes: "",
      // POAs array (multiple pieces of art per SKU)
      poas: [createDefaultPoa(1)],
      // Finishing notes (applies to all POAs)
      finishingNotes: "",
      // Assembly
      assemblyNotes: "",
    }
  ]);

  // Add new SKU - inherits settings from the last SKU for faster data entry
  const addSku = () => {
    setSkus(prev => {
      const lastSku = prev[prev.length - 1];
      const newId = prev.length + 1;

      // Deep copy POAs from last SKU, clearing names/notes
      const inheritedPoas = lastSku?.poas?.map((poa, idx) => ({
        ...poa,
        id: idx + 1,
        name: `POA ${idx + 1}`,
        notes: "",
        workCenters: { ...poa.workCenters },
        finishing: { ...poa.finishing },
      })) || [createDefaultPoa(1)];

      const newSku = {
        id: newId,
        description: "",
        quantity: 1,
        expanded: true,
        // Inherit work types
        needsModel: lastSku?.needsModel ?? false,
        needsPaint: lastSku?.needsPaint ?? false,
        needsGraphics: lastSku?.needsGraphics ?? true,
        // Inherit structure settings
        structureType: lastSku?.structureType ?? "",
        structurePieces: lastSku?.structurePieces ?? 1,
        structureNotes: "",
        // Inherit paint settings
        paintFinish: lastSku?.paintFinish ?? "",
        paintOpacity: lastSku?.paintOpacity ?? "",
        paintEffect: lastSku?.paintEffect ?? "",
        colorTarget: lastSku?.colorTarget ?? "",
        pantoneNumber: "",
        paintNotes: "",
        // POAs array (inherited from last SKU)
        poas: inheritedPoas,
        finishingNotes: "",
        assemblyNotes: "",
      };

      return [...prev, newSku];
    });
  };

  // Remove SKU
  const removeSku = (skuId) => {
    if (skus.length > 1) {
      setSkus(prev => prev.filter(s => s.id !== skuId));
    }
  };

  // Update SKU field
  const updateSku = (skuId, field, value) => {
    setSkus(prev => prev.map(s => s.id === skuId ? { ...s, [field]: value } : s));
  };

  // POA Management Functions
  const addPoaToSku = (skuId) => {
    setSkus(prev => prev.map(sku => {
      if (sku.id !== skuId) return sku;
      const newPoaId = Math.max(...sku.poas.map(p => p.id), 0) + 1;
      // Inherit settings from last POA in this SKU
      const lastPoa = sku.poas[sku.poas.length - 1];
      const newPoa = lastPoa ? {
        ...createDefaultPoa(newPoaId),
        substrate: lastPoa.substrate,
        appliedFinish: lastPoa.appliedFinish,
        specialEffects: lastPoa.specialEffects,
        workCenters: { ...lastPoa.workCenters },
        finishing: { ...lastPoa.finishing },
      } : createDefaultPoa(newPoaId);
      return { ...sku, poas: [...sku.poas, newPoa] };
    }));
  };

  const removePoaFromSku = (skuId, poaId) => {
    setSkus(prev => prev.map(sku => {
      if (sku.id !== skuId) return sku;
      if (sku.poas.length <= 1) return sku; // Keep at least one POA
      return { ...sku, poas: sku.poas.filter(p => p.id !== poaId) };
    }));
  };

  const updatePoa = (skuId, poaId, field, value) => {
    setSkus(prev => prev.map(sku => {
      if (sku.id !== skuId) return sku;
      return {
        ...sku,
        poas: sku.poas.map(poa =>
          poa.id === poaId ? { ...poa, [field]: value } : poa
        )
      };
    }));
  };

  const togglePoaExpanded = (skuId, poaId) => {
    setSkus(prev => prev.map(sku => {
      if (sku.id !== skuId) return sku;
      return {
        ...sku,
        poas: sku.poas.map(poa =>
          poa.id === poaId ? { ...poa, expanded: !poa.expanded } : poa
        )
      };
    }));
  };

  // Toggle SKU expansion
  const toggleSkuExpanded = (skuId) => {
    setSkus(prev => prev.map(s => s.id === skuId ? { ...s, expanded: !s.expanded } : s));
  };

  // Copy settings from one SKU to another (preserves id, description, quantity, expanded)
  const copySettingsFromSku = (targetSkuId, sourceSkuId) => {
    const sourceSku = skus.find(s => s.id === sourceSkuId);
    if (!sourceSku) return;

    // Fields to preserve on the target
    const preserveFields = ['id', 'description', 'quantity', 'expanded'];

    setSkus(prev => prev.map(s => {
      if (s.id !== targetSkuId) return s;
      const updated = { ...s };
      Object.keys(sourceSku).forEach(key => {
        if (!preserveFields.includes(key)) {
          // Handle POAs array specially for deep copy
          if (key === 'poas' && Array.isArray(sourceSku[key])) {
            updated[key] = sourceSku[key].map(p => ({
              ...p,
              workCenters: { ...p.workCenters },
              finishing: { ...p.finishing }
            }));
          } else if (typeof sourceSku[key] === 'object' && sourceSku[key] !== null) {
            updated[key] = { ...sourceSku[key] };
          } else {
            updated[key] = sourceSku[key];
          }
        }
      });
      return updated;
    }));
  };

  // Apply a field value to all SKUs
  const applyFieldToAllSkus = (field, value) => {
    setSkus(prev => prev.map(s => ({
      ...s,
      [field]: typeof value === 'object' && value !== null ? { ...value } : value
    })));
  };

  // Toggle accordion section
  const toggleSection = (section) => {
    setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
  };

  // Tab 3 state - Active Projects from job_stages API
  const [activeProjects, setActiveProjects] = useState([]);
  const [activeProjectsLoading, setActiveProjectsLoading] = useState(true);
  const [activeProjectsError, setActiveProjectsError] = useState(null);
  const [selectedProject, setSelectedProject] = useState(null);
  const [activeWorkspaceTab, setActiveWorkspaceTab] = useState("overview");
  const [projectFilter, setProjectFilter] = useState("all");
  const [projectSearch, setProjectSearch] = useState("");

  // Fetch active projects from job_stages unified endpoint
  const loadActiveProjects = useCallback(async () => {
    setActiveProjectsLoading(true);
    setActiveProjectsError(null);

    try {
      const accessToken = await acquireAccessToken();
      if (!accessToken) {
        setActiveProjectsLoading(false);
        return;
      }

      // Fetch from unified PA endpoint which includes all projects with jobs
      const response = await fetch(
        `${import.meta.env.VITE_API_URL || ""}/api/unified/pa/dashboard`,
        {
          headers: { Authorization: `Bearer ${accessToken}` },
        }
      );

      if (!response.ok) {
        throw new Error(`Failed to fetch projects: ${response.status}`);
      }

      const data = await response.json();

      // Transform to expected shape for Active Projects tab
      const transformedProjects = (data.projects || []).map((project) => {
        // Map canonical stage to status
        const stageToStatus = {
          PENDING: "prep",
          PROD_ART: "proofing",
          PRINTING: "production",
          ASSEMBLY: "finishing",
          SHIPPING: "complete",
        };

        // Calculate progress based on stage (rough estimate)
        const stageProgress = {
          PENDING: 10,
          PROD_ART: 30,
          PRINTING: 60,
          ASSEMBLY: 80,
          SHIPPING: 100,
        };

        return {
          id: project.quoteNumber,
          project_id: project.orderNumber || `Q-${project.quoteNumber}`,
          quote_number: project.quoteNumber,
          customer_name: project.customerName,
          description: project.projectName,
          status: stageToStatus[project.worstStage] || "prep",
          ship_date: project.shipDate,
          progress: stageProgress[project.worstStage] || 0,
          assigned_to: project.pm ? { id: project.pm, name: project.pm } : null,
          // Jobs from Job Progress Contract
          jobs: project.jobs || [],
          jobCount: project.jobCount,
          totalQty: project.totalQty,
          worstStage: project.worstStage,
          lastUpdated: project.lastUpdated,
          orders: [], // Not available from this endpoint
        };
      });

      // Filter to only show non-complete projects
      const activeOnly = transformedProjects.filter(
        (p) => p.worstStage !== "SHIPPING"
      );

      setActiveProjects(activeOnly);
    } catch (err) {
      console.error("Error fetching active projects:", err);
      setActiveProjectsError(err.message);
      // Fall back to mock data on error
      setActiveProjects(MOCK_ACTIVE_PROJECTS);
    } finally {
      setActiveProjectsLoading(false);
    }
  }, [acquireAccessToken]);

  // Initial load of active projects
  useEffect(() => {
    loadActiveProjects();
  }, [loadActiveProjects]);

  // Stats for tabs
  const stats = useMemo(() => ({
    unassignedKickoffs: kickoffs.length,
    assignedAwaiting: assigned.length,
    workOrderDrafts: workOrderDrafts.length,
    activeProjects: activeProjects.filter(p => p.status !== "complete").length,
  }), [kickoffs, assigned, workOrderDrafts, activeProjects]);

  // Format relative time
  const formatRelativeTime = (dateStr) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now - date;
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (diffHours < 1) return "Just now";
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays === 1) return "Yesterday";
    return `${diffDays} days ago`;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "—";
    const d = new Date(dateStr);
    return d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
  };

  // Handle assignment
  const handleAssign = (kickoffId, pmId) => {
    const pm = PM_TEAM.find(p => p.id === pmId);
    const kickoff = kickoffs.find(k => k.id === kickoffId);

    if (kickoff && pm) {
      // Move from kickoffs to assigned
      setKickoffs(prev => prev.filter(k => k.id !== kickoffId));
      setAssigned(prev => [...prev, {
        ...kickoff,
        assigned_to: { id: pm.id, name: pm.name },
        assigned_at: new Date().toISOString().split('T')[0],
        status: "assigned",
      }]);
    }
    setShowAssignModal(null);
  };

  // Handle claim (self-assign)
  const handleClaim = (kickoffId) => {
    handleAssign(kickoffId, CURRENT_USER.id);
  };

  // Filter active projects
  const filteredProjects = useMemo(() => {
    let filtered = [...activeProjects];

    if (projectFilter !== "all") {
      filtered = filtered.filter(p => p.status === projectFilter);
    }

    if (projectSearch.trim()) {
      const query = projectSearch.toLowerCase();
      filtered = filtered.filter(p =>
        p.project_id.toLowerCase().includes(query) ||
        p.customer_name.toLowerCase().includes(query) ||
        p.description.toLowerCase().includes(query)
      );
    }

    return filtered;
  }, [activeProjects, projectFilter, projectSearch]);

  // Render Tab 1: Quotes/Projects
  const renderQuotesTab = () => (
    <div className="p-4 space-y-6 overflow-y-auto flex-1">
      {/* Unassigned Kickoffs */}
      <section>
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-semibold text-red-400 flex items-center gap-2">
            <span className="w-2 h-2 bg-red-500 rounded-full animate-pulse" />
            NEW KICKOFFS - UNASSIGNED
            <span className="text-gray-500">({kickoffs.length})</span>
          </h3>
        </div>

        {loading.intakes ? (
          <div className="bg-gray-200 rounded-lg p-6 text-center text-gray-500 border border-white/5">
            <span className="animate-pulse">Loading email intakes...</span>
          </div>
        ) : kickoffs.length === 0 ? (
          <div className="bg-gray-200 rounded-lg p-6 text-center text-gray-500 border border-white/5">
            No unassigned kickoffs
          </div>
        ) : (
          <div className="space-y-2">
            {kickoffs.map(kickoff => (
              <div key={kickoff.id} className="bg-gray-200 rounded-lg p-4 border border-red-500/20 hover:border-red-500/40 transition-colors">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="font-mono text-sm text-purple-600">{kickoff.quote_number}</span>
                      <span className="text-sm font-medium">{kickoff.customer_name}</span>
                      <span className="text-xs text-gray-500">• {formatRelativeTime(kickoff.kicked_off_at)}</span>
                    </div>
                    <div className="text-sm text-gray-700 mb-2">{kickoff.description}</div>
                    <div className="flex items-center gap-4 text-xs text-gray-500">
                      <span>Sales: {kickoff.sales_rep}</span>
                      <span>Ship: {formatDate(kickoff.ship_date)}</span>
                      <span>
                        {kickoff.quantities.shrinks > 0 && `${kickoff.quantities.shrinks} shrinks`}
                        {kickoff.quantities.cartons > 0 && ` ${kickoff.quantities.cartons} cartons`}
                        {kickoff.quantities.labels > 0 && ` ${kickoff.quantities.labels} labels`}
                      </span>
                    </div>
                    {kickoff.attachments?.length > 0 && (
                      <div className="flex items-center gap-2 mt-2">
                        {kickoff.attachments.map((att, idx) => (
                          <span key={idx} className="inline-flex items-center gap-1 px-2 py-1 bg-slate-100 rounded text-xs">
                            <span>📎</span> {att.name}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="relative">
                      <button
                        onClick={() => setShowAssignModal(showAssignModal === kickoff.id ? null : kickoff.id)}
                        className="px-3 py-1.5 bg-gray-200 hover:bg-gray-200 rounded text-sm transition-colors"
                      >
                        Assign ▼
                      </button>
                      {showAssignModal === kickoff.id && (
                        <div className="absolute right-0 top-full mt-1 bg-slate-100 border border-gray-200 rounded-lg shadow-xl z-10 py-1 min-w-[150px]">
                          {PM_TEAM.map(pm => (
                            <button
                              key={pm.id}
                              onClick={() => handleAssign(kickoff.id, pm.id)}
                              className="w-full px-3 py-2 text-left text-sm hover:bg-slate-100 transition-colors"
                            >
                              {pm.name}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                    <button
                      onClick={() => handleClaim(kickoff.id)}
                      className="px-3 py-1.5 bg-blue-600 hover:bg-blue-700 rounded text-sm font-medium transition-colors"
                    >
                      Claim
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* Assigned - Awaiting Work Order */}
      <section>
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-semibold text-yellow-400">
            ASSIGNED - AWAITING WORK ORDER
            <span className="text-gray-500 ml-2">({assigned.length})</span>
          </h3>
        </div>

        {assigned.length === 0 ? (
          <div className="bg-gray-200 rounded-lg p-6 text-center text-gray-500 border border-white/5">
            No projects awaiting work orders
          </div>
        ) : (
          <div className="space-y-2">
            {assigned.map(project => (
              <div key={project.id} className="bg-gray-200 rounded-lg p-4 border border-yellow-500/20 hover:border-yellow-500/40 transition-colors">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="font-mono text-sm text-purple-600">{project.quote_number}</span>
                      <span className="text-sm font-medium">{project.customer_name}</span>
                      <span className={`px-1.5 py-0.5 rounded text-xs ${project.art_status === "received" ? "bg-green-600" : "bg-yellow-600"}`}>
                        {project.art_status === "received" ? "✓ Art received" : "⏳ Awaiting art"}
                      </span>
                    </div>
                    <div className="text-sm text-gray-700 mb-2">{project.description}</div>
                    <div className="flex items-center gap-4 text-xs text-gray-500">
                      <span>Assigned: {project.assigned_to.name}</span>
                      <span>Ship: {formatDate(project.ship_date)}</span>
                    </div>
                  </div>
                  <button
                    onClick={() => {
                      setSelectedWorkOrder(project);
                      setActiveMainTab("workorders");
                    }}
                    className="px-4 py-2 bg-purple-600 hover:bg-purple-700 rounded text-sm font-medium transition-colors"
                  >
                    Create Work Order →
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* Pipeline Preview */}
      <section>
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-semibold text-gray-500">
            PIPELINE (Not Yet Kicked Off)
            <span className="text-gray-500 ml-2">({MOCK_PIPELINE_QUOTES.length})</span>
          </h3>
        </div>

        <div className="bg-gray-200 rounded-lg border border-white/5">
          {MOCK_PIPELINE_QUOTES.map((quote, idx) => (
            <div
              key={quote.id}
              className={`flex items-center justify-between p-3 ${idx !== MOCK_PIPELINE_QUOTES.length - 1 ? "border-b border-white/5" : ""}`}
            >
              <div className="flex items-center gap-3">
                <span className="text-gray-500">⚪</span>
                <span className="font-mono text-xs text-gray-500">{quote.quote_number}</span>
                <span className="text-sm">{quote.customer_name}</span>
                <span className="text-xs text-gray-500">{quote.description}</span>
              </div>
              <div className="text-xs text-gray-500">
                Est. Ship: {formatDate(quote.estimated_ship)}
              </div>
            </div>
          ))}
        </div>
      </section>
    </div>
  );

  // Reusable Accordion Section component
  const AccordionSection = ({ id, title, icon, children, badge }) => (
    <div className="bg-gray-200 rounded-lg border border-gray-200 overflow-hidden">
      <button
        onClick={() => toggleSection(id)}
        className="w-full px-4 py-3 flex items-center justify-between hover:bg-slate-200 transition-colors"
      >
        <div className="flex items-center gap-2">
          <span>{icon}</span>
          <span className="font-semibold">{title}</span>
          {badge && <span className="px-2 py-0.5 bg-gray-200 rounded text-xs">{badge}</span>}
        </div>
        <span className="text-gray-500">{expandedSections[id] ? "▼" : "▶"}</span>
      </button>
      {expandedSections[id] && (
        <div className="px-4 pb-4 border-t border-white/5">
          {children}
        </div>
      )}
    </div>
  );

  // Render Header Section content
  const renderHeaderSection = () => (
    <div className="pt-4 space-y-4">
      <div className="grid grid-cols-3 gap-4">
        <div>
          <label className="block text-xs text-gray-500 mb-1">Project ID</label>
          <input
            type="text"
            defaultValue={selectedWorkOrder?.project_id || ""}
            placeholder="Auto-generated"
            className="w-full px-3 py-2 bg-slate-100 border border-gray-300 rounded text-sm"
          />
        </div>
        <div>
          <label className="block text-xs text-gray-500 mb-1">JobBoss Order #</label>
          <input
            type="text"
            placeholder="e.g., 2000892"
            className="w-full px-3 py-2 bg-slate-100 border border-gray-300 rounded text-sm"
          />
        </div>
        <div>
          <label className="block text-xs text-gray-500 mb-1">Quote Number</label>
          <input
            type="text"
            defaultValue={selectedWorkOrder?.quote_number || ""}
            className="w-full px-3 py-2 bg-slate-100 border border-gray-300 rounded text-sm"
            readOnly
          />
        </div>
      </div>
      <div>
        <label className="block text-xs text-gray-500 mb-1">Project Description</label>
        <input
          type="text"
          defaultValue={selectedWorkOrder?.description || ""}
          className="w-full px-3 py-2 bg-slate-100 border border-gray-300 rounded text-sm"
        />
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-xs text-gray-500 mb-1">Customer</label>
          <input
            type="text"
            defaultValue={selectedWorkOrder?.customer_name || ""}
            className="w-full px-3 py-2 bg-slate-100 border border-gray-300 rounded text-sm"
          />
        </div>
        <div>
          <label className="block text-xs text-gray-500 mb-1">Brand</label>
          <input
            type="text"
            placeholder="Brand name"
            className="w-full px-3 py-2 bg-slate-100 border border-gray-300 rounded text-sm"
          />
        </div>
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-xs text-gray-500 mb-1">Project Manager</label>
          <select className="w-full px-3 py-2 bg-slate-100 border border-gray-300 rounded text-sm">
            <option value="">Select PM</option>
            {PM_TEAM.map(pm => (
              <option key={pm.id} value={pm.id}>{pm.name}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-xs text-gray-500 mb-1">Sales Rep</label>
          <select className="w-full px-3 py-2 bg-slate-100 border border-gray-300 rounded text-sm">
            <option value="">Select Sales Rep</option>
            {SALES_REPS.map(rep => (
              <option key={rep} value={rep}>{rep}</option>
            ))}
          </select>
        </div>
      </div>
      <div className="grid grid-cols-3 gap-4">
        <div>
          <label className="block text-xs text-gray-500 mb-1">Total SKUs</label>
          <input
            type="number"
            value={skuCount}
            onChange={(e) => setSkuCount(parseInt(e.target.value) || 1)}
            min="1"
            className="w-full px-3 py-2 bg-slate-100 border border-gray-300 rounded text-sm"
          />
        </div>
        <div>
          <label className="block text-xs text-gray-500 mb-1">Ship Date</label>
          <input
            type="date"
            defaultValue={selectedWorkOrder?.ship_date || ""}
            className="w-full px-3 py-2 bg-slate-100 border border-gray-300 rounded text-sm"
          />
        </div>
        <div>
          <label className="block text-xs text-gray-500 mb-1">Packed & Ready Time</label>
          <select className="w-full px-3 py-2 bg-slate-100 border border-gray-300 rounded text-sm">
            {["8:00 AM", "8:30 AM", "9:00 AM", "9:30 AM", "10:00 AM", "10:30 AM", "11:00 AM",
              "11:30 AM", "12:00 PM", "12:30 PM", "1:00 PM", "1:30 PM", "2:00 PM", "2:30 PM",
              "3:00 PM", "3:30 PM", "4:00 PM", "4:30 PM", "5:00 PM"].map(t => (
              <option key={t} value={t}>{t}</option>
            ))}
          </select>
        </div>
      </div>
      <div>
        <label className="block text-xs text-gray-500 mb-1">Project Contact Info</label>
        <textarea
          rows={2}
          placeholder="Contact name, email, phone"
          className="w-full px-3 py-2 bg-slate-100 border border-gray-300 rounded text-sm"
        />
      </div>
      <div>
        <label className="block text-xs text-gray-500 mb-1">PM Notes</label>
        <textarea
          rows={2}
          placeholder="Internal notes for the production team"
          className="w-full px-3 py-2 bg-slate-100 border border-gray-300 rounded text-sm"
        />
      </div>
    </div>
  );

  // NEW: Compact table row for SKU with work center columns
  const renderSkuTableRow = (sku, index) => {
    // Get summary of what's configured for each work center
    const getGraphicsSummary = () => {
      if (!sku.needsGraphics) return "—";
      const poas = sku.poas || [];
      if (poas.length === 0) return "Configure...";

      // Count finishing options across all POAs
      const finishing = [];
      if (poas.some(p => p.finishing?.opti)) finishing.push("Opti");
      if (poas.some(p => p.finishing?.franklin)) finishing.push("Frank");

      const finishStr = finishing.length > 0 ? ` (${finishing.join(", ")})` : "";
      return `${poas.length} POA${poas.length !== 1 ? 's' : ''}${finishStr}`;
    };

    const getModelSummary = () => {
      if (!sku.needsModel) return "—";
      return sku.structureType || "Configure...";
    };

    const getPaintSummary = () => {
      if (!sku.needsPaint) return "—";
      const parts = [];
      if (sku.paintFinish) parts.push(sku.paintFinish);
      if (sku.pantoneNumber) parts.push(sku.pantoneNumber);
      return parts.length > 0 ? parts.join(", ") : "Configure...";
    };

    const getAssemblySummary = () => {
      if (!sku.needsModel && !sku.needsPaint && !sku.needsGraphics) return "—";
      return sku.assemblyNotes ? sku.assemblyNotes.substring(0, 20) + "..." : "—";
    };

    return (
      <div key={sku.id} className="bg-gray-200 rounded-lg border border-gray-200 overflow-hidden">
        {/* Compact Table Row */}
        <div
          className="grid grid-cols-12 gap-2 px-3 py-2 items-center cursor-pointer hover:bg-slate-200 transition-colors text-sm"
          onClick={() => toggleSkuExpanded(sku.id)}
        >
          {/* Expand indicator + SKU # */}
          <div className="col-span-1 flex items-center gap-1">
            <span className="text-gray-500 text-xs">{sku.expanded ? "▼" : "▶"}</span>
            <span className="text-emerald-400 font-medium">#{sku.id}</span>
          </div>

          {/* Description + Qty */}
          <div className="col-span-3 flex items-center gap-2 truncate">
            <span className="text-gray-800 truncate">{sku.description || <span className="text-gray-500 italic">No description</span>}</span>
            <span className="text-gray-500 text-xs shrink-0">×{sku.quantity}</span>
          </div>

          {/* Work Center Columns */}
          <div className={`col-span-2 text-xs px-2 py-1 rounded ${sku.needsGraphics ? "bg-purple-900/40 text-purple-600" : "text-gray-600"}`}>
            {getGraphicsSummary()}
          </div>
          <div className={`col-span-2 text-xs px-2 py-1 rounded ${sku.needsModel ? "bg-blue-100 text-blue-600" : "text-gray-600"}`}>
            {getModelSummary()}
          </div>
          <div className={`col-span-2 text-xs px-2 py-1 rounded ${sku.needsPaint ? "bg-orange-900/40 text-orange-600" : "text-gray-600"}`}>
            {getPaintSummary()}
          </div>
          <div className="col-span-1 text-xs text-gray-500 truncate">
            {getAssemblySummary()}
          </div>

          {/* Actions */}
          <div className="col-span-1 flex items-center justify-end gap-1" onClick={(e) => e.stopPropagation()}>
            {skus.length > 1 && (
              <select
                onChange={(e) => {
                  if (e.target.value) {
                    copySettingsFromSku(sku.id, parseInt(e.target.value));
                    e.target.value = '';
                  }
                }}
                className="w-16 px-1 py-0.5 text-[10px] bg-gray-200 border border-gray-300 rounded text-gray-700"
                title="Copy settings from another SKU"
              >
                <option value="">Copy...</option>
                {skus.filter(s => s.id !== sku.id).map(s => (
                  <option key={s.id} value={s.id}>#{s.id}</option>
                ))}
              </select>
            )}
            {skus.length > 1 && (
              <button
                onClick={() => removeSku(sku.id)}
                className="text-red-400 hover:text-red-600 text-xs px-1"
                title="Remove SKU"
              >×</button>
            )}
          </div>
        </div>

        {/* Expanded: Horizontal Work Center Panels */}
        {sku.expanded && (
          <div className="border-t border-gray-200">
            {/* Quick row: Description + Qty + Work Type Toggles */}
            <div className="px-3 py-2 bg-slate-200 flex items-center gap-4 border-b border-white/5">
              <div className="flex-1 flex items-center gap-3">
                <input
                  type="text"
                  value={sku.description}
                  onChange={(e) => updateSku(sku.id, "description", e.target.value)}
                  placeholder="SKU Description"
                  className="flex-1 px-2 py-1 bg-slate-100 border border-gray-300 rounded text-sm"
                />
                <div className="flex items-center gap-1">
                  <span className="text-xs text-gray-500">Qty:</span>
                  <input
                    type="number"
                    value={sku.quantity}
                    onChange={(e) => updateSku(sku.id, "quantity", parseInt(e.target.value) || 1)}
                    min="1"
                    className="w-16 px-2 py-1 bg-slate-100 border border-gray-300 rounded text-sm"
                  />
                </div>
              </div>
              <div className="flex items-center gap-3 text-sm">
                <label className="flex items-center gap-1.5 cursor-pointer">
                  <input type="checkbox" checked={sku.needsGraphics} onChange={(e) => updateSku(sku.id, "needsGraphics", e.target.checked)} className="w-3.5 h-3.5" />
                  <span className="text-purple-600">Graphics</span>
                </label>
                <label className="flex items-center gap-1.5 cursor-pointer">
                  <input type="checkbox" checked={sku.needsModel} onChange={(e) => updateSku(sku.id, "needsModel", e.target.checked)} className="w-3.5 h-3.5" />
                  <span className="text-blue-400">Model</span>
                </label>
                <label className="flex items-center gap-1.5 cursor-pointer">
                  <input type="checkbox" checked={sku.needsPaint} onChange={(e) => updateSku(sku.id, "needsPaint", e.target.checked)} className="w-3.5 h-3.5" />
                  <span className="text-orange-400">Paint</span>
                </label>
              </div>
              {/* Apply to All button */}
              {skus.length > 1 && (
                <button
                  onClick={() => {
                    const fieldsToApply = ['needsGraphics', 'needsModel', 'needsPaint'];
                    fieldsToApply.forEach(field => applyFieldToAllSkus(field, sku[field]));
                    // Deep copy POAs to all other SKUs
                    setSkus(prev => prev.map(s =>
                      s.id === sku.id ? s : { ...s, poas: sku.poas.map(p => ({ ...p, workCenters: { ...p.workCenters }, finishing: { ...p.finishing } })) }
                    ));
                  }}
                  className="px-2 py-1 text-[10px] bg-emerald-600/30 hover:bg-emerald-600/50 text-emerald-300 rounded"
                  title="Apply this SKU's settings to all other SKUs"
                >
                  Apply to All
                </button>
              )}
            </div>

            {/* Horizontal Work Center Panels */}
            <div className="grid grid-cols-4 divide-x divide-gray-200">
              {/* GRAPHICS / PROD ART Panel - Multiple POAs */}
              <div className={`p-3 space-y-2 ${sku.needsGraphics ? "bg-purple-900/10" : "bg-slate-200 opacity-50"}`}>
                <div className="flex items-center justify-between border-b border-purple-500/30 pb-1 mb-2">
                  <span className="text-xs font-medium text-purple-600">
                    GRAPHICS / PROD ART
                    {sku.needsGraphics && sku.poas?.length > 0 && (
                      <span className="ml-1 text-gray-500">({sku.poas.length} POA{sku.poas.length !== 1 ? 's' : ''})</span>
                    )}
                  </span>
                  {sku.needsGraphics && (
                    <button
                      onClick={() => addPoaToSku(sku.id)}
                      className="text-[10px] px-1.5 py-0.5 bg-purple-600/30 hover:bg-purple-600/50 text-purple-600 rounded"
                    >
                      + POA
                    </button>
                  )}
                </div>
                {sku.needsGraphics ? (
                  <div className="space-y-2 max-h-64 overflow-y-auto">
                    {(sku.poas || []).map((poa, poaIdx) => (
                      <div key={poa.id} className="bg-gray-200 rounded border border-purple-500/20 overflow-hidden">
                        {/* POA Header */}
                        <div
                          className="px-2 py-1.5 flex items-center justify-between cursor-pointer hover:bg-slate-200"
                          onClick={() => togglePoaExpanded(sku.id, poa.id)}
                        >
                          <div className="flex items-center gap-2 flex-1 min-w-0">
                            <span className="text-gray-500 text-[10px]">{poa.expanded ? "▼" : "▶"}</span>
                            <input
                              type="text"
                              value={poa.name}
                              onChange={(e) => { e.stopPropagation(); updatePoa(sku.id, poa.id, "name", e.target.value); }}
                              onClick={(e) => e.stopPropagation()}
                              placeholder="POA name..."
                              className="flex-1 px-1 py-0.5 bg-transparent border-b border-transparent hover:border-gray-300 focus:border-purple-500 text-xs text-purple-600 outline-none min-w-0"
                            />
                          </div>
                          <div className="flex items-center gap-1 shrink-0">
                            <span className="text-[9px] text-gray-500">
                              {poa.substrate?.split(' ')[0] || ''}
                              {poa.finishing?.opti && ', Opti'}
                            </span>
                            {(sku.poas?.length || 0) > 1 && (
                              <button
                                onClick={(e) => { e.stopPropagation(); removePoaFromSku(sku.id, poa.id); }}
                                className="text-red-400 hover:text-red-600 text-xs px-1"
                              >×</button>
                            )}
                          </div>
                        </div>
                        {/* POA Details */}
                        {poa.expanded && (
                          <div className="px-2 py-2 border-t border-purple-500/10 space-y-2">
                            <div className="grid grid-cols-2 gap-2">
                              <div>
                                <label className="block text-[9px] text-gray-500 mb-0.5">POA Type</label>
                                <select value={poa.poaType} onChange={(e) => updatePoa(sku.id, poa.id, "poaType", e.target.value)} className="w-full px-1 py-0.5 bg-slate-100 border border-gray-300 rounded text-[10px]">
                                  <option value="">Select...</option>
                                  {WO_OPTIONS.poaTypes.map(t => <option key={t} value={t}>{t}</option>)}
                                </select>
                              </div>
                              <div>
                                <label className="block text-[9px] text-gray-500 mb-0.5">Substrate</label>
                                <select value={poa.substrate} onChange={(e) => updatePoa(sku.id, poa.id, "substrate", e.target.value)} className="w-full px-1 py-0.5 bg-slate-100 border border-gray-300 rounded text-[10px]">
                                  <option value="">Select...</option>
                                  {WO_OPTIONS.substrates.map(t => <option key={t} value={t} disabled={t.startsWith("---")}>{t}</option>)}
                                </select>
                              </div>
                            </div>
                            <div className="grid grid-cols-2 gap-2">
                              <div>
                                <label className="block text-[9px] text-gray-500 mb-0.5">Finish</label>
                                <select value={poa.appliedFinish} onChange={(e) => updatePoa(sku.id, poa.id, "appliedFinish", e.target.value)} className="w-full px-1 py-0.5 bg-slate-100 border border-gray-300 rounded text-[10px]">
                                  <option value="">Select...</option>
                                  {WO_OPTIONS.finishes.map(t => <option key={t} value={t}>{t}</option>)}
                                </select>
                              </div>
                              <div>
                                <label className="block text-[9px] text-gray-500 mb-0.5">Effects</label>
                                <select value={poa.specialEffects} onChange={(e) => updatePoa(sku.id, poa.id, "specialEffects", e.target.value)} className="w-full px-1 py-0.5 bg-slate-100 border border-gray-300 rounded text-[10px]">
                                  <option value="">None</option>
                                  {WO_OPTIONS.specialEffects.map(t => <option key={t} value={t}>{t}</option>)}
                                </select>
                              </div>
                            </div>
                            <div className="flex flex-wrap gap-x-2 gap-y-1 pt-1 border-t border-white/5">
                              {[{ key: "epson", label: "Epson" }, { key: "silver", label: "Silver" }, { key: "os", label: "O/S" }, { key: "uv", label: "UV" }].map(opt => (
                                <label key={opt.key} className="flex items-center gap-1 text-[10px]">
                                  <input type="checkbox" checked={poa.workCenters?.[opt.key]} onChange={(e) => updatePoa(sku.id, poa.id, "workCenters", { ...poa.workCenters, [opt.key]: e.target.checked })} className="w-2.5 h-2.5" />
                                  <span>{opt.label}</span>
                                </label>
                              ))}
                              <span className="text-gray-600 mx-1">|</span>
                              <label className="flex items-center gap-1 text-[10px]">
                                <input type="checkbox" checked={poa.finishing?.opti} onChange={(e) => updatePoa(sku.id, poa.id, "finishing", { ...poa.finishing, opti: e.target.checked })} className="w-2.5 h-2.5" />
                                <span>Opti</span>
                              </label>
                              <label className="flex items-center gap-1 text-[10px]">
                                <input type="checkbox" checked={poa.finishing?.franklin} onChange={(e) => updatePoa(sku.id, poa.id, "finishing", { ...poa.finishing, franklin: e.target.checked })} className="w-2.5 h-2.5" />
                                <span>Franklin</span>
                              </label>
                            </div>
                            <textarea value={poa.notes} onChange={(e) => updatePoa(sku.id, poa.id, "notes", e.target.value)} rows={1} placeholder="POA notes..." className="w-full px-1 py-0.5 bg-slate-100 border border-gray-300 rounded text-[10px]" />
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-xs text-gray-500 italic py-4 text-center">Not required</div>
                )}
              </div>

              {/* MODEL Panel */}
              <div className={`p-3 space-y-2 ${sku.needsModel ? "bg-blue-900/10" : "bg-slate-200 opacity-50"}`}>
                <div className="text-xs font-medium text-blue-400 border-b border-blue-500/30 pb-1 mb-2">
                  MODEL / 3D
                </div>
                {sku.needsModel ? (
                  <>
                    <div>
                      <label className="block text-[10px] text-gray-500 mb-0.5">Structure Type</label>
                      <select value={sku.structureType} onChange={(e) => updateSku(sku.id, "structureType", e.target.value)} className="w-full px-1.5 py-1 bg-slate-100 border border-gray-300 rounded text-xs">
                        <option value="">Select...</option>
                        {WO_OPTIONS.structureTypes.map(t => <option key={t} value={t}>{t}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className="block text-[10px] text-gray-500 mb-0.5"># of Pieces</label>
                      <input type="number" value={sku.structurePieces} onChange={(e) => updateSku(sku.id, "structurePieces", parseInt(e.target.value) || 1)} min="1" className="w-full px-1.5 py-1 bg-slate-100 border border-gray-300 rounded text-xs" />
                    </div>
                    <div>
                      <label className="block text-[10px] text-gray-500 mb-0.5">Source</label>
                      <select className="w-full px-1.5 py-1 bg-slate-100 border border-gray-300 rounded text-xs">
                        <option value="client">Client provides</option>
                        <option value="source">PM to source</option>
                        <option value="3d">3D Team makes</option>
                      </select>
                    </div>
                    <textarea value={sku.structureNotes} onChange={(e) => updateSku(sku.id, "structureNotes", e.target.value)} rows={2} placeholder="Structure notes..." className="w-full px-1.5 py-1 bg-slate-100 border border-gray-300 rounded text-xs" />
                  </>
                ) : (
                  <div className="text-xs text-gray-500 italic py-4 text-center">Not required</div>
                )}
              </div>

              {/* PAINT Panel */}
              <div className={`p-3 space-y-2 ${sku.needsPaint ? "bg-orange-900/10" : "bg-slate-200 opacity-50"}`}>
                <div className="text-xs font-medium text-orange-400 border-b border-orange-500/30 pb-1 mb-2">
                  PAINT / COLOR
                </div>
                {sku.needsPaint ? (
                  <>
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <label className="block text-[10px] text-gray-500 mb-0.5">Color Target</label>
                        <input type="text" value={sku.colorTarget} onChange={(e) => updateSku(sku.id, "colorTarget", e.target.value)} placeholder="Reference" className="w-full px-1.5 py-1 bg-slate-100 border border-gray-300 rounded text-xs" />
                      </div>
                      <div>
                        <label className="block text-[10px] text-gray-500 mb-0.5">Pantone</label>
                        <input type="text" value={sku.pantoneNumber} onChange={(e) => updateSku(sku.id, "pantoneNumber", e.target.value)} placeholder="PMS #" className="w-full px-1.5 py-1 bg-slate-100 border border-gray-300 rounded text-xs" />
                      </div>
                    </div>
                    <div>
                      <label className="block text-[10px] text-gray-500 mb-0.5">Finish</label>
                      <select value={sku.paintFinish} onChange={(e) => updateSku(sku.id, "paintFinish", e.target.value)} className="w-full px-1.5 py-1 bg-slate-100 border border-gray-300 rounded text-xs">
                        <option value="">Select...</option>
                        {WO_OPTIONS.paintFinishes.map(t => <option key={t} value={t}>{t}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className="block text-[10px] text-gray-500 mb-0.5">Opacity</label>
                      <select value={sku.paintOpacity} onChange={(e) => updateSku(sku.id, "paintOpacity", e.target.value)} className="w-full px-1.5 py-1 bg-slate-100 border border-gray-300 rounded text-xs">
                        <option value="">Select...</option>
                        {WO_OPTIONS.opacities.map(t => <option key={t} value={t}>{t}</option>)}
                      </select>
                    </div>
                    <textarea value={sku.paintNotes} onChange={(e) => updateSku(sku.id, "paintNotes", e.target.value)} rows={2} placeholder="Paint notes..." className="w-full px-1.5 py-1 bg-slate-100 border border-gray-300 rounded text-xs" />
                  </>
                ) : (
                  <div className="text-xs text-gray-500 italic py-4 text-center">Not required</div>
                )}
              </div>

              {/* ASSEMBLY Panel */}
              <div className="p-3 space-y-2 bg-slate-200">
                <div className="text-xs font-medium text-gray-500 border-b border-gray-500/30 pb-1 mb-2">
                  ASSEMBLY
                </div>
                {(sku.needsModel || sku.needsPaint || sku.needsGraphics) ? (
                  <>
                    <textarea
                      value={sku.assemblyNotes}
                      onChange={(e) => updateSku(sku.id, "assemblyNotes", e.target.value)}
                      rows={4}
                      placeholder="How pieces come together..."
                      className="w-full px-1.5 py-1 bg-slate-100 border border-gray-300 rounded text-xs"
                    />
                    <div className="text-[10px] text-gray-500 pt-1">
                      Finishing notes:
                    </div>
                    <textarea
                      value={sku.finishingNotes}
                      onChange={(e) => updateSku(sku.id, "finishingNotes", e.target.value)}
                      rows={2}
                      placeholder="Final finishing..."
                      className="w-full px-1.5 py-1 bg-slate-100 border border-gray-300 rounded text-xs"
                    />
                  </>
                ) : (
                  <div className="text-xs text-gray-500 italic py-4 text-center">No work types selected</div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  // Render SKUs Section content - NEW TABLE FORMAT
  const renderSkusSection = () => (
    <div className="pt-4 space-y-2">
      {/* Header row with column labels */}
      <div className="flex items-center justify-between mb-2">
        <div className="text-sm text-gray-500">
          {skus.length} SKU{skus.length !== 1 ? 's' : ''} •
          {skus.filter(s => s.needsModel).length > 0 && ` ${skus.filter(s => s.needsModel).length} Model`}
          {skus.filter(s => s.needsPaint).length > 0 && ` ${skus.filter(s => s.needsPaint).length} Paint`}
          {skus.filter(s => s.needsGraphics).length > 0 && ` ${skus.filter(s => s.needsGraphics).length} Graphics`}
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setSkus(prev => prev.map(s => ({ ...s, expanded: false })))}
            className="px-2 py-1 text-xs text-gray-500 hover:text-gray-800 transition-colors"
          >
            Collapse All
          </button>
          <button
            onClick={() => setSkus(prev => prev.map(s => ({ ...s, expanded: true })))}
            className="px-2 py-1 text-xs text-gray-500 hover:text-gray-800 transition-colors"
          >
            Expand All
          </button>
          <button
            onClick={addSku}
            className="px-3 py-1.5 bg-emerald-600 hover:bg-emerald-700 rounded text-xs font-medium transition-colors"
            title="New SKU inherits settings from the previous SKU"
          >
            + Add SKU
          </button>
        </div>
      </div>

      {/* Table header */}
      <div className="grid grid-cols-12 gap-2 px-3 py-1.5 text-[10px] uppercase tracking-wider text-gray-500 bg-white rounded-t border-b border-gray-200">
        <div className="col-span-1">SKU</div>
        <div className="col-span-3">Description</div>
        <div className="col-span-2 text-purple-600">Graphics</div>
        <div className="col-span-2 text-blue-400">Model</div>
        <div className="col-span-2 text-orange-400">Paint</div>
        <div className="col-span-1">Assy</div>
        <div className="col-span-1 text-right">Actions</div>
      </div>

      {/* SKU Rows */}
      <div className="space-y-1">
        {skus.map((sku, index) => renderSkuTableRow(sku, index))}
      </div>
    </div>
  );

  // Render Shipping Section content
  const renderShippingSection = () => (
    <div className="pt-4 space-y-4">
      {[1, 2, 3].map(shipNum => (
        <div key={shipNum} className="bg-slate-100 rounded-lg p-4 space-y-3">
          <h4 className="font-medium text-blue-600">Shipment #{shipNum}</h4>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs text-gray-500 mb-1">Shipping Method</label>
              <select className="w-full px-2 py-1.5 bg-gray-200 border border-gray-300 rounded text-sm">
                <option value="">Select method...</option>
                {WO_OPTIONS.shippingMethods.map(m => <option key={m} value={m}>{m}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">Ship To Address</label>
              <textarea
                rows={3}
                placeholder="Name&#10;Address&#10;City, State ZIP&#10;Phone"
                className="w-full px-2 py-1.5 bg-gray-200 border border-gray-300 rounded text-sm"
              />
            </div>
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">Shipping Notes</label>
            <input
              type="text"
              placeholder="e.g., SHIP 4 BOTTLES - MUST ARRIVE NLT 12/12"
              className="w-full px-2 py-1.5 bg-gray-200 border border-gray-300 rounded text-sm"
            />
          </div>
        </div>
      ))}
      <button className="px-3 py-1.5 bg-gray-200 hover:bg-gray-200 rounded text-xs transition-colors">
        + Add More Shipments
      </button>
    </div>
  );

  // Render Proofing Section content
  const renderProofingSection = () => (
    <div className="pt-4 space-y-4">
      <div className="bg-slate-100 rounded-lg p-4 space-y-4">
        <h4 className="font-medium text-green-600">Proofing Type</h4>
        <div className="grid grid-cols-2 gap-x-8 gap-y-2">
          {[
            "Internal Review Only",
            "Layered PDF",
            "PDF w/ Thumbnails",
            "Flat Print - NO Finishes",
            "Flat - With Finishes",
            "Assembled - NO Finishes",
            "Other"
          ].map(opt => (
            <label key={opt} className="flex items-center gap-2 text-sm">
              <input type="checkbox" className="w-4 h-4" />
              <span>{opt}</span>
            </label>
          ))}
        </div>
        <div>
          <label className="block text-xs text-gray-500 mb-1">Proofing Notes</label>
          <textarea
            rows={2}
            placeholder="Email addresses for PDF proofs, special proofing instructions"
            className="w-full px-2 py-1.5 bg-gray-200 border border-gray-300 rounded text-sm"
          />
        </div>
      </div>

      <div className="bg-slate-100 rounded-lg p-4 space-y-4">
        <h4 className="font-medium text-cyan-600">Structure Requirements</h4>
        <div className="grid grid-cols-2 gap-x-8 gap-y-2">
          {[
            "Structure Required - YES",
            "Structure N/A",
            "PM to source retail/in-market product",
            "Sales Rep to source",
            "Client to provide",
            "3D Team to make and/or paint"
          ].map(opt => (
            <label key={opt} className="flex items-center gap-2 text-sm">
              <input type="checkbox" className="w-4 h-4" />
              <span>{opt}</span>
            </label>
          ))}
        </div>
        <div>
          <label className="block text-xs text-gray-500 mb-1">Structure Notes</label>
          <textarea
            rows={2}
            placeholder="UPC codes, sourcing details, assembly notes"
            className="w-full px-2 py-1.5 bg-gray-200 border border-gray-300 rounded text-sm"
          />
        </div>
      </div>
    </div>
  );

  // Render Tab 2: Work Orders
  const renderWorkOrdersTab = () => (
    <div className="flex-1 flex overflow-hidden">
      {/* Left: Drafts & Change Orders */}
      <div className="w-72 border-r border-gray-200 flex flex-col bg-slate-200">
        <div className="p-3 border-b border-gray-200">
          <h3 className="text-sm font-semibold">Work Orders</h3>
        </div>

        <div className="flex-1 overflow-y-auto p-3 space-y-4">
          {/* In Progress Drafts */}
          <div>
            <h4 className="text-xs font-semibold text-gray-500 mb-2">IN PROGRESS ({workOrderDrafts.length})</h4>
            {loading.workOrders ? (
              <div className="text-xs text-gray-500 p-2 animate-pulse">Loading...</div>
            ) : workOrderDrafts.length === 0 ? (
              <div className="text-xs text-gray-500 p-2">No drafts</div>
            ) : (
              <div className="space-y-2">
                {workOrderDrafts.map(wo => (
                  <div
                    key={wo.id}
                    onClick={() => setSelectedWorkOrder(wo)}
                    className={`p-3 rounded-lg cursor-pointer transition-colors ${
                      selectedWorkOrder?.id === wo.id
                        ? "bg-purple-600/20 border border-purple-500/50"
                        : "bg-gray-200 border border-white/5 hover:border-gray-300"
                    }`}
                  >
                    <div className="font-mono text-xs text-purple-600">{wo.wo_number || wo.project_id}</div>
                    <div className="text-sm font-medium mt-1">{wo.customer_name}</div>
                    <div className="text-xs text-gray-500 mt-1">{wo.project_name || wo.description}</div>
                    {wo.item_count && (
                      <div className="text-xs text-gray-500 mt-1">{wo.item_count} SKUs</div>
                    )}
                    {wo.progress !== undefined && (
                      <div className="flex items-center gap-2 mt-2">
                        <div className="flex-1 h-1.5 bg-gray-200 rounded-full">
                          <div
                            className="h-full bg-purple-500 rounded-full"
                            style={{ width: `${wo.progress}%` }}
                          />
                        </div>
                        <span className="text-xs text-gray-500">{wo.progress}%</span>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* From Assigned Queue */}
          <div>
            <h4 className="text-xs font-semibold text-gray-500 mb-2">READY TO CREATE</h4>
            {assigned.filter(a => a.art_status === "received").map(project => (
              <div
                key={project.id}
                onClick={() => setSelectedWorkOrder(project)}
                className={`p-3 rounded-lg cursor-pointer transition-colors ${
                  selectedWorkOrder?.id === project.id
                    ? "bg-purple-600/20 border border-purple-500/50"
                    : "bg-gray-200 border border-white/5 hover:border-gray-300"
                }`}
              >
                <div className="font-mono text-xs text-green-400">{project.quote_number}</div>
                <div className="text-sm font-medium mt-1">{project.customer_name}</div>
                <div className="text-xs text-gray-500 mt-1">✓ Art received</div>
              </div>
            ))}
          </div>

          {/* Active Work Orders from Database */}
          <div>
            <h4 className="text-xs font-semibold text-gray-500 mb-2">
              ACTIVE ORDERS ({workOrders.filter(wo => wo.status !== "draft").length})
            </h4>
            {workOrders.filter(wo => wo.status !== "draft").map(wo => (
              <div
                key={wo.id}
                onClick={() => setSelectedWorkOrder(wo)}
                className={`p-3 rounded-lg cursor-pointer transition-colors ${
                  selectedWorkOrder?.id === wo.id
                    ? "bg-emerald-600/20 border border-emerald-500/50"
                    : "bg-gray-200 border border-white/5 hover:border-gray-300"
                }`}
              >
                <div className="flex items-center justify-between">
                  <div className="font-mono text-xs text-emerald-400">{wo.wo_number}</div>
                  {wo.is_rush && <span className="px-1.5 py-0.5 bg-red-600 rounded text-[10px]">RUSH</span>}
                </div>
                <div className="text-sm font-medium mt-1">{wo.customer_name}</div>
                <div className="text-xs text-gray-500 mt-1">{wo.project_name}</div>
                <div className="flex items-center gap-2 mt-1 text-xs">
                  <span className={`px-1.5 py-0.5 rounded ${
                    wo.status === "in_production" ? "bg-green-600" :
                    wo.status === "submitted" ? "bg-blue-600" : "bg-gray-200"
                  }`}>{wo.status}</span>
                  {wo.item_count && <span className="text-gray-500">{wo.item_count} SKUs</span>}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Right: Work Order Form */}
      <div className="flex-1 overflow-y-auto bg-slate-100/20">
        {selectedWorkOrder ? (
          <div className="p-6 max-w-5xl mx-auto">
            {/* Form Header */}
            <div className="flex items-center justify-between mb-4">
              <div>
                <h2 className="text-xl font-semibold">{selectedWorkOrder.customer_name}</h2>
                <p className="text-sm text-gray-500">{selectedWorkOrder.description || selectedWorkOrder.quote_number}</p>
              </div>
              <div className="flex items-center gap-3">
                <span className="px-3 py-1 bg-yellow-600 rounded text-sm">Draft</span>
              </div>
            </div>

            {/* Layout Toggle */}
            <div className="flex items-center justify-between mb-4 p-3 bg-white rounded-lg border border-gray-200">
              <div className="flex items-center gap-4">
                <span className="text-sm text-gray-500">SKU-Centric Work Order</span>
                <div className="flex gap-2 text-xs">
                  <span className="flex items-center gap-1"><span className="w-2 h-2 bg-blue-500 rounded-full"></span> Model</span>
                  <span className="flex items-center gap-1"><span className="w-2 h-2 bg-orange-500 rounded-full"></span> Paint</span>
                  <span className="flex items-center gap-1"><span className="w-2 h-2 bg-purple-500 rounded-full"></span> Graphics</span>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-500">Layout:</span>
                <div className="flex gap-1 bg-slate-100 rounded p-0.5">
                  <button
                    onClick={() => setWoFormLayout("accordion")}
                    className={`px-3 py-1 rounded text-xs transition-colors ${
                      woFormLayout === "accordion" ? "bg-emerald-600 text-gray-900" : "text-gray-500 hover:text-gray-900"
                    }`}
                  >
                    Accordion
                  </button>
                  <button
                    onClick={() => setWoFormLayout("tabs")}
                    className={`px-3 py-1 rounded text-xs transition-colors ${
                      woFormLayout === "tabs" ? "bg-emerald-600 text-gray-900" : "text-gray-500 hover:text-gray-900"
                    }`}
                  >
                    Tabs
                  </button>
                </div>
              </div>
            </div>

            {/* ACCORDION LAYOUT */}
            {woFormLayout === "accordion" && (
              <div className="space-y-3">
                <AccordionSection id="header" title="Project Header" icon="📋">
                  {renderHeaderSection()}
                </AccordionSection>

                {/* SKUs Section - Now contains all work types per SKU */}
                <div className="bg-gray-200 rounded-lg border border-gray-200 overflow-hidden">
                  <div className="px-4 py-3 flex items-center justify-between bg-emerald-900/30 border-b border-emerald-500/30">
                    <div className="flex items-center gap-2">
                      <span>📦</span>
                      <span className="font-semibold">SKUs & Work Requirements</span>
                      <span className="px-2 py-0.5 bg-emerald-600/50 rounded text-xs">
                        {skus.length} SKU{skus.length !== 1 ? 's' : ''}
                      </span>
                    </div>
                    <div className="flex gap-2 text-xs">
                      {skus.filter(s => s.needsModel).length > 0 && (
                        <span className="px-2 py-0.5 bg-blue-600/50 rounded">{skus.filter(s => s.needsModel).length} Model</span>
                      )}
                      {skus.filter(s => s.needsPaint).length > 0 && (
                        <span className="px-2 py-0.5 bg-orange-600/50 rounded">{skus.filter(s => s.needsPaint).length} Paint</span>
                      )}
                      {skus.filter(s => s.needsGraphics).length > 0 && (
                        <span className="px-2 py-0.5 bg-purple-600/50 rounded">{skus.filter(s => s.needsGraphics).length} Graphics</span>
                      )}
                    </div>
                  </div>
                  <div className="p-4">
                    {renderSkusSection()}
                  </div>
                </div>

                <AccordionSection id="shipping" title="Shipping Information" icon="📦">
                  {renderShippingSection()}
                </AccordionSection>

                <AccordionSection id="proofing" title="Proofing & Structure Requirements" icon="✅">
                  {renderProofingSection()}
                </AccordionSection>
              </div>
            )}

            {/* TABBED LAYOUT */}
            {woFormLayout === "tabs" && (
              <div>
                {/* Tab Navigation */}
                <div className="flex gap-1 mb-4 border-b border-gray-200 pb-1">
                  {[
                    { id: "header", label: "Header", icon: "📋" },
                    { id: "skus", label: `SKUs (${skus.length})`, icon: "📦" },
                    { id: "shipping", label: "Shipping", icon: "🚚" },
                    { id: "proofing", label: "Proofing", icon: "✅" }
                  ].map(tab => (
                    <button
                      key={tab.id}
                      onClick={() => setWoFormTab(tab.id)}
                      className={`px-4 py-2 rounded-t text-sm font-medium transition-colors ${
                        woFormTab === tab.id
                          ? "bg-emerald-600 text-gray-900"
                          : "text-gray-500 hover:text-gray-900 hover:bg-slate-200"
                      }`}
                    >
                      {tab.icon} {tab.label}
                    </button>
                  ))}
                </div>

                {/* Tab Content */}
                <div className="bg-gray-200 rounded-lg border border-gray-200 p-4">
                  {woFormTab === "header" && renderHeaderSection()}
                  {woFormTab === "skus" && renderSkusSection()}
                  {woFormTab === "shipping" && renderShippingSection()}
                  {woFormTab === "proofing" && renderProofingSection()}
                </div>
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex items-center justify-between pt-6 mt-6 border-t border-gray-200">
              <button className="px-4 py-2 bg-gray-200 hover:bg-gray-200 rounded text-sm transition-colors">
                Save Draft
              </button>
              <div className="flex items-center gap-3">
                <button className="px-4 py-2 bg-blue-600 hover:bg-blue-700 rounded text-sm transition-colors">
                  Preview Work Order
                </button>
                <button className="px-6 py-2 bg-green-600 hover:bg-green-700 rounded text-sm font-medium transition-colors">
                  Create Work Order & Add to Tracker
                </button>
              </div>
            </div>
          </div>
        ) : (
          <div className="flex-1 flex items-center justify-center text-gray-500 h-full">
            <div className="text-center">
              <div className="text-4xl mb-2">📋</div>
              <div>Select a project to create or edit a work order</div>
            </div>
          </div>
        )}
      </div>
    </div>
  );

  // Render Tab 3: Active Projects
  const renderActiveProjectsTab = () => {
    // Loading state
    if (activeProjectsLoading) {
      return (
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-500 mx-auto mb-4" />
            <p className="text-gray-500">Loading active projects...</p>
          </div>
        </div>
      );
    }

    // Error state with retry
    if (activeProjectsError && activeProjects.length === 0) {
      return (
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <p className="text-red-400 mb-4">Error: {activeProjectsError}</p>
            <button
              onClick={loadActiveProjects}
              className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 rounded text-sm"
            >
              Retry
            </button>
          </div>
        </div>
      );
    }

    return (
    <div className="flex-1 flex flex-col overflow-hidden">
      {/* Data Source Indicator */}
      {activeProjectsError && (
        <div className="px-3 py-1 bg-yellow-100 text-yellow-800 text-xs flex items-center gap-2">
          <span>Using cached data - live sync unavailable</span>
        </div>
      )}

      {/* Filters Bar */}
      <div className="p-3 border-b border-gray-200 flex items-center gap-4 bg-white">
        <input
          type="text"
          placeholder="Search projects..."
          value={projectSearch}
          onChange={(e) => setProjectSearch(e.target.value)}
          className="w-64 px-3 py-1.5 bg-slate-100 border border-gray-300 rounded text-sm"
        />
        <select
          value={projectFilter}
          onChange={(e) => setProjectFilter(e.target.value)}
          className="px-3 py-1.5 bg-slate-100 border border-gray-300 rounded text-sm"
        >
          <option value="all">All Statuses</option>
          <option value="prep">Prep</option>
          <option value="production">Production</option>
          <option value="proofing">Proofing</option>
          <option value="finishing">Finishing</option>
          <option value="complete">Complete</option>
        </select>
        <div className="flex-1" />
        <span className="text-sm text-gray-500">{filteredProjects.length} projects</span>
        <button
          onClick={loadActiveProjects}
          disabled={activeProjectsLoading}
          className="px-3 py-1.5 text-sm text-gray-600 hover:text-gray-900 hover:bg-slate-100 rounded transition-colors disabled:opacity-50"
          title="Refresh data"
        >
          {activeProjectsLoading ? (
            <span className="inline-block animate-spin">⟳</span>
          ) : (
            "⟳ Refresh"
          )}
        </button>
      </div>

      {/* Split View: Table + Workspace */}
      <div className="flex-1 flex overflow-hidden">
        {/* Left Panel: Project Table + Quality Widget */}
        <div className={`${selectedProject ? "w-1/2" : "flex-1"} flex flex-col border-r border-gray-200`}>
          {/* Project Table */}
          <div className="flex-1 overflow-y-auto">
            <table className="w-full text-sm">
              <thead className="bg-white sticky top-0">
                <tr className="text-left text-xs text-gray-500">
                  <th className="p-3">Project</th>
                  <th className="p-3">Customer</th>
                  <th className="p-3">Status</th>
                  <th className="p-3">Jobs</th>
                  <th className="p-3">Ship</th>
                  <th className="p-3">Progress</th>
                  <th className="p-3">PM</th>
                </tr>
              </thead>
            <tbody>
              {filteredProjects.map(project => (
                <tr
                  key={project.id}
                  onClick={() => {
                    setSelectedProject(project);
                    setActiveWorkspaceTab("overview");
                  }}
                  className={`border-b border-white/5 cursor-pointer transition-colors ${
                    selectedProject?.id === project.id
                      ? "bg-purple-600/20"
                      : "hover:bg-slate-200"
                  }`}
                >
                  <td className="p-3">
                    <div className="font-mono text-purple-600">{project.project_id}</div>
                  </td>
                  <td className="p-3">
                    <div className="font-medium">{project.customer_name}</div>
                    <div className="text-xs text-gray-500 truncate max-w-[200px]">{project.description}</div>
                  </td>
                  <td className="p-3">
                    <span className={`px-2 py-1 rounded text-xs ${STATUS_CONFIG[project.status]?.color || "bg-gray-200"}`}>
                      {STATUS_CONFIG[project.status]?.label || project.status}
                    </span>
                  </td>
                  <td className="p-3 text-center">
                    <span className="text-gray-700">{project.jobCount || project.jobs?.length || 0}</span>
                    {project.totalQty > 0 && (
                      <span className="text-xs text-gray-400 ml-1">({project.totalQty.toLocaleString()})</span>
                    )}
                  </td>
                  <td className="p-3 text-gray-700">{formatDate(project.ship_date)}</td>
                  <td className="p-3">
                    <div className="flex items-center gap-2">
                      <div className="w-16 h-2 bg-gray-200 rounded-full">
                        <div
                          className="h-full bg-green-500 rounded-full"
                          style={{ width: `${project.progress}%` }}
                        />
                      </div>
                      <span className="text-xs text-gray-500">{project.progress}%</span>
                    </div>
                  </td>
                  <td className="p-3 text-gray-700">{project.assigned_to?.name}</td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>

          {/* Quality Issues Widget - v2.1 */}
          <div className="border-t border-gray-200 bg-white p-3">
            <PMReworkWidget
              onViewEvent={(eventId) => console.log('View rework event:', eventId)}
            />
          </div>
        </div>

        {/* Project Workspace */}
        {selectedProject && (
          <div className="w-1/2 flex flex-col overflow-hidden bg-slate-100/20">
            {/* Workspace Header */}
            <div className="p-4 border-b border-gray-200 bg-white">
              <div className="flex items-center justify-between">
                <div>
                  <div className="flex items-center gap-2">
                    <span className="font-mono text-lg text-purple-600">{selectedProject.project_id}</span>
                    <span className={`px-2 py-0.5 rounded text-xs ${STATUS_CONFIG[selectedProject.status]?.color}`}>
                      {STATUS_CONFIG[selectedProject.status]?.label}
                    </span>
                  </div>
                  <h3 className="text-lg font-semibold mt-1">{selectedProject.customer_name}</h3>
                </div>
                <button
                  onClick={() => setSelectedProject(null)}
                  className="p-2 hover:bg-slate-100 rounded transition-colors"
                >
                  ✕
                </button>
              </div>

              {/* Workspace Tabs */}
              <div className="flex gap-1 mt-4">
                {WORKSPACE_TABS.map(tab => (
                  <button
                    key={tab.id}
                    onClick={() => setActiveWorkspaceTab(tab.id)}
                    className={`px-3 py-1.5 rounded text-sm transition-colors ${
                      activeWorkspaceTab === tab.id
                        ? "bg-purple-600 text-gray-900"
                        : "text-gray-500 hover:text-gray-900 hover:bg-slate-100"
                    }`}
                  >
                    {tab.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Workspace Content */}
            <div className="flex-1 overflow-y-auto p-4">
              {activeWorkspaceTab === "overview" && (
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="bg-gray-200 rounded-lg p-3 border border-white/5">
                      <div className="text-xs text-gray-500">Ship Date</div>
                      <div className="text-lg font-medium mt-1">{formatDate(selectedProject.ship_date)}</div>
                    </div>
                    <div className="bg-gray-200 rounded-lg p-3 border border-white/5">
                      <div className="text-xs text-gray-500">Progress</div>
                      <div className="text-lg font-medium mt-1">{selectedProject.progress}%</div>
                    </div>
                  </div>

                  <div className="bg-gray-200 rounded-lg p-3 border border-white/5">
                    <div className="text-xs text-gray-500 mb-2">Description</div>
                    <div className="text-sm">{selectedProject.description}</div>
                  </div>

                  {selectedProject.orders?.length > 0 && (
                    <div className="bg-gray-200 rounded-lg p-3 border border-white/5">
                      <div className="text-xs text-gray-500 mb-2">Orders & Work Orders</div>
                      {selectedProject.orders.map((order, idx) => (
                        <div key={idx} className="text-sm">
                          <span className="font-mono text-blue-400">{order.order_number}</span>
                          <span className="text-gray-500 ml-2">
                            ({order.work_orders.join(", ")})
                          </span>
                        </div>
                      ))}
                    </div>
                  )}

                  <div className="flex gap-3">
                    <button className="px-4 py-2 bg-orange-600 hover:bg-orange-700 rounded text-sm transition-colors">
                      Create Change Order
                    </button>
                    <button className="px-4 py-2 bg-gray-200 hover:bg-gray-200 rounded text-sm transition-colors">
                      Add Note
                    </button>
                  </div>
                </div>
              )}

              {activeWorkspaceTab === "art_files" && (
                <div className="text-center py-8 text-gray-500">
                  <div className="text-4xl mb-2">📁</div>
                  <div>Art files workspace</div>
                  <button className="mt-3 px-4 py-2 bg-blue-600 hover:bg-blue-700 rounded text-sm transition-colors">
                    Upload Files
                  </button>
                </div>
              )}

              {activeWorkspaceTab === "orders" && (
                <div className="space-y-4">
                  {selectedProject.orders?.length > 0 ? (
                    selectedProject.orders.map((order, idx) => (
                      <div key={idx} className="bg-gray-200 rounded-lg p-4 border border-white/5">
                        <div className="flex items-center justify-between mb-2">
                          <span className="font-mono text-blue-400">{order.order_number}</span>
                          <button className="text-xs text-blue-400 hover:text-blue-600">View in JobBOSS</button>
                        </div>
                        <div className="space-y-1">
                          {order.work_orders.map((wo, woIdx) => (
                            <div key={woIdx} className="text-sm text-gray-700 pl-4 border-l-2 border-gray-300">
                              {wo}
                            </div>
                          ))}
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="text-center py-8 text-gray-500">
                      No orders created yet
                    </div>
                  )}
                </div>
              )}

              {activeWorkspaceTab === "proofs" && (
                <div className="text-center py-8 text-gray-500">
                  <div className="text-4xl mb-2">📄</div>
                  <div>Proofs workspace</div>
                  <button className="mt-3 px-4 py-2 bg-purple-600 hover:bg-purple-700 rounded text-sm transition-colors">
                    Generate Proof PDF
                  </button>
                </div>
              )}

              {activeWorkspaceTab === "notes" && (
                <div className="space-y-4">
                  <button className="px-4 py-2 bg-blue-600 hover:bg-blue-700 rounded text-sm transition-colors">
                    + Add Note
                  </button>
                  <div className="text-center py-8 text-gray-500">
                    No notes yet
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
  };

  return (
    <div className="min-h-screen bg-slate-100 text-gray-900 flex flex-col">
      {/* Header */}
      <header className="w-full bg-gradient-to-r from-emerald-600 via-teal-500 to-cyan-500 shadow-lg">
        <div className="max-w-full mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-baseline gap-2">
            <h1 className="text-xl font-semibold tracking-wide">3D COLOR</h1>
            <span className="text-xs opacity-80 uppercase">PM Command Center</span>
            <span className="ml-2 px-2 py-0.5 bg-slate-100 rounded text-xs">Option E: 3-Tab Structure</span>
          </div>

          <div className="flex items-center gap-3">
            <span className="text-sm opacity-80">Logged in as: {CURRENT_USER.name}</span>
            <button
              onClick={onBackToApp}
              className="px-4 py-1.5 bg-slate-100 hover:bg-slate-200 rounded-md text-sm font-medium transition-colors"
            >
              ← Time Tracker
            </button>
          </div>
        </div>
      </header>

      {/* Main Tabs */}
      <div className="border-b border-gray-200 bg-white">
        <div className="max-w-full mx-auto px-4">
          <div className="flex">
            <button
              onClick={() => setActiveMainTab("quotes")}
              className={`px-6 py-3 text-sm font-medium border-b-2 transition-colors ${
                activeMainTab === "quotes"
                  ? "border-emerald-400 text-gray-900"
                  : "border-transparent text-gray-500 hover:text-gray-900"
              }`}
            >
              <span className="flex items-center gap-2">
                📋 Quotes / Projects
                {(stats.unassignedKickoffs + stats.assignedAwaiting) > 0 && (
                  <span className="px-2 py-0.5 bg-red-500 rounded-full text-xs">
                    {stats.unassignedKickoffs + stats.assignedAwaiting}
                  </span>
                )}
              </span>
            </button>
            <button
              onClick={() => setActiveMainTab("workorders")}
              className={`px-6 py-3 text-sm font-medium border-b-2 transition-colors ${
                activeMainTab === "workorders"
                  ? "border-emerald-400 text-gray-900"
                  : "border-transparent text-gray-500 hover:text-gray-900"
              }`}
            >
              <span className="flex items-center gap-2">
                ✍️ Work Orders
                {stats.workOrderDrafts > 0 && (
                  <span className="px-2 py-0.5 bg-yellow-500 text-black rounded-full text-xs">
                    {stats.workOrderDrafts}
                  </span>
                )}
              </span>
            </button>
            <button
              onClick={() => setActiveMainTab("active")}
              className={`px-6 py-3 text-sm font-medium border-b-2 transition-colors ${
                activeMainTab === "active"
                  ? "border-emerald-400 text-gray-900"
                  : "border-transparent text-gray-500 hover:text-gray-900"
              }`}
            >
              <span className="flex items-center gap-2">
                📊 Active Projects
                <span className="px-2 py-0.5 bg-gray-200 rounded-full text-xs">
                  {stats.activeProjects}
                </span>
              </span>
            </button>
          </div>
        </div>
      </div>

      {/* Tab Content */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {activeMainTab === "quotes" && renderQuotesTab()}
        {activeMainTab === "workorders" && renderWorkOrdersTab()}
        {activeMainTab === "active" && renderActiveProjectsTab()}
      </div>
    </div>
  );
}
